import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PatientPortal from './pages/PatientPortal';
import ChangePasswordGate from './components/patient-portal/ChangePasswordGate';
import { API_BASE, AUTH_AUDIENCE } from './config/env';
import { AuthLoginPage } from './auth/client';

function ProtectedPortal() {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout, error, user, mustChangePassword } =
    useAuth0();
  const [role, setRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } }).catch(() => {});
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setRole(null);
      setLoadingRole(false);
      setRoleError(null);
      return;
    }
    let active = true;
    setLoadingRole(true);

    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH_AUDIENCE },
        });
        const res = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const message =
            res.status === 403
              ? 'Your account is not provisioned in this backend organization.'
              : res.status === 404
                ? 'User profile was not found in backend.'
                : `Backend returned ${res.status}.`;
          throw new Error(message);
        }
        const payload = await res.json();
        if (active) {
          setRole(String(payload?.role || '').toUpperCase());
          setRoleError(null);
        }
      } catch (e: any) {
        if (active) {
          setRole('');
          const raw = String(e?.message || 'Unable to verify user role.');
          if (raw.toLowerCase().includes('failed to fetch') || raw.toLowerCase().includes('networkerror')) {
            setRoleError(`Cannot reach backend at ${API_BASE}. Is the API running?`);
          } else {
            setRoleError(raw);
          }
        }
      } finally {
        if (active) setLoadingRole(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  if (isLoading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-rose-600">Auth error: {error.message}</div>;
  if (!isAuthenticated) return <div className="p-6">Redirecting to login...</div>;

  // GL-M16: block the entire authenticated app until the patient sets a new
  // password. This takes priority over role checks and portal data loading.
  if (mustChangePassword) return <ChangePasswordGate />;

  if (loadingRole) return <div className="p-6">Loading...</div>;

  if (role !== 'PATIENT') {
    return (
      <div className="p-6 space-y-4">
        <div className="text-rose-600">This application is only available for patient accounts.</div>
        {roleError ? (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{roleError}</div>
        ) : null}
        <div className="text-sm text-slate-600">
          Signed in as <strong>{user?.email || 'unknown account'}</strong>
          {role ? <> with role <strong>{role}</strong></> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border px-3 py-2 text-sm"
            onClick={() =>
              loginWithRedirect({
                authorizationParams: { audience: AUTH_AUDIENCE, prompt: 'login' },
                appState: { returnTo: '/' },
              }).catch(() => {})
            }
          >
            Switch account
          </button>
          <button
            type="button"
            className="rounded border px-3 py-2 text-sm"
            onClick={() =>
              logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  return <PatientPortal />;
}

export default function App() {
  const location = useLocation();
  if (location.pathname === '/login') {
    return <AuthLoginPage variant="portal" />;
  }

  return <ProtectedPortal />;
}
